import { ExternalLink, Info, MessageCircle } from 'lucide-react';
import { useState } from 'react';

import { Avatar } from '@/design-system/ui/Avatar';
import { Button } from '@/design-system/ui/Button';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from '@/design-system/ui/Dialog';
import { formatShortDate } from '@escala/domain/dates';
import { formatPhone } from '@escala/domain/members';
import { whatsappLink } from '@escala/domain/reminders';

/**
 * Cobrança de um pendente pelo WhatsApp: mostra quem recebe (nome, telefone e evento) e a
 * mensagem pronta, que dá para editar. "Abrir no WhatsApp" abre o wa.me numa nova aba com
 * o número (+55) e o texto; o envio sai do aparelho de quem cobra, e o sistema só registra
 * que o WhatsApp foi aberto.
 */
export default function WhatsAppDialog({
  name,
  photo,
  phone,
  assignment,
  event,
  message,
  onOpen,
  onClose,
}: {
  name: string;
  photo: string | null;
  /** Só os dígitos, com DDD. */
  phone: string;
  /** "Recepção · Portaria" */
  assignment: string;
  event: { title: string; date: string; time: string };
  message: string;
  onOpen: () => void;
  onClose: () => void;
}) {
  const [text, setText] = useState(message);
  const empty = !text.trim();

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[min(520px,calc(100vw-24px))]">
        <DialogHeader title="Cobrar pelo WhatsApp" description="Confirmação de presença pendente" />
        <DialogBody className="gap-3">
          <div
            aria-label="Destinatário"
            className="flex items-center gap-3 rounded-block border border-line-card bg-surface-subtle px-3.5 py-3"
          >
            <Avatar name={name} photo={photo} size="lg" className="size-9" />
            <div className="min-w-0 flex-1">
              <p className="m-0 text-control font-semibold text-ink">{name}</p>
              <p className="m-0 text-meta text-ink-secondary">
                <span data-numeric>+55 {formatPhone(phone)}</span> · {assignment}
              </p>
              <p className="m-0 text-caption text-ink-subtle">
                {event.title} · {formatShortDate(event.date)} · {event.time}
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between gap-2">
              <label htmlFor="whatsapp-message" className="text-small font-semibold">
                Mensagem
              </label>
              <span data-numeric aria-live="polite" className="text-caption text-ink-subtle">
                {text.length} {text.length === 1 ? 'caractere' : 'caracteres'}
              </span>
            </div>
            <textarea
              id="whatsapp-message"
              rows={9}
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full min-w-0 resize-y rounded-control border border-line-input bg-white px-3 py-2.5 text-control text-ink placeholder:text-ink-placeholder"
            />
          </div>
          <p className="m-0 flex gap-2 rounded-control bg-surface-muted px-3 py-2 text-meta text-ink-muted">
            <Info aria-hidden className="mt-0.5 size-3.5 shrink-0 stroke-[1.8]" />
            Abre o WhatsApp com a mensagem pronta; o envio é feito por você. O sistema não consegue confirmar
            se ela foi enviada.
          </p>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary">Cancelar</Button>
          </DialogClose>
          <Button
            className="bg-success hover:bg-status-complete disabled:hover:bg-success"
            disabled={empty}
            onClick={() => {
              window.open(whatsappLink(phone, text.trim()), '_blank', 'noopener,noreferrer');
              onOpen();
            }}
          >
            <MessageCircle aria-hidden />
            Abrir no WhatsApp
            <ExternalLink aria-hidden />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
